import React, { useState, useEffect, useCallback } from 'react';
import {
  FaBell,
  FaTimes,
  FaTrash,
  FaInfoCircle,
  FaExclamationTriangle,
  FaCheckDouble
} from 'react-icons/fa';
import notificationService from '../services/notificationService';
import { useNavigate } from 'react-router-dom';
import '../styles/NotificationsPage.css';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterType, setFilterType] = useState('');
  const [filterReadStatus, setFilterReadStatus] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [searchText, setSearchText] = useState('');
  const itemsPerPage = 10;

  const NOTIFICATION_TYPES = {
    user_request_pending: 'User Request Pending',
    asset_request_submitted: 'Asset Request Submitted',
    technician_accepted_issue: 'Issue Accepted',
    technician_completed_issue: 'Issue Completed',
    breakdown_request_submitted: 'Breakdown Request',
    maintenance_due_soon: 'Maintenance Due Soon',
    asset_low_stock: 'Low Stock Alert',
    request_approved: 'Request Approved',
    asset_revoked: 'Asset Revoked',
    issue_assigned: 'Issue Assigned',
    maintenance_assigned: 'Maintenance Assigned',
    issue_closed: 'Issue Closed'
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return '#d32f2f';
      case 'warning':
        return '#f57c00';
      case 'info':
        return '#1976d2';
      default:
        return '#757575';
    }
  };

  // Determine severity from notification object or type
  const getNotificationSeverity = (notification) => {
    // First, use the severity field if it exists
    if (notification.severity) {
      return notification.severity;
    }

    // Fallback to determining based on notification type
    const lowStockTypes = ['asset_low_stock', 'maintenance_due_soon'];
    const highTypes = ['technician_completed_issue', 'issue_assigned'];
    const warningTypes = ['breakdown_request_submitted', 'asset_revoked'];

    if (lowStockTypes.includes(notification.type)) {
      return 'warning';
    }
    if (highTypes.includes(notification.type)) {
      return 'high';
    }
    if (warningTypes.includes(notification.type)) {
      return 'warning';
    }

    return 'info'; // default
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high':
        return <FaExclamationTriangle style={{ color: getSeverityColor('high') }} />;
      case 'warning':
        return <FaExclamationTriangle style={{ color: getSeverityColor('warning') }} />;
      case 'info':
        return <FaInfoCircle style={{ color: getSeverityColor('info') }} />;
      default:
        return <FaBell />;
    }
  };

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const isRead = filterReadStatus === '' ? null : filterReadStatus === 'read';
      const response = await notificationService.getNotifications(
        filterType || null,
        isRead,
        page,
        itemsPerPage
      );

      if (response.notifications) {
        setNotifications(response.notifications);
        setTotalPages(response.totalPages || Math.ceil(response.total / itemsPerPage));
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [page, filterType, filterReadStatus, itemsPerPage]);

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationService.getUnreadCount();
      setUnreadCount(response.count || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Fetch notifications
  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
  }, [fetchNotifications]);

  // Mark as read
  const handleMarkAsRead = async (notificationId, isRead) => {
    try {
      await notificationService.markAsRead(notificationId);
      setNotifications(notifications.map(notif =>
        notif._id === notificationId ? { ...notif, isRead: true } : notif
      ));
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      fetchNotifications();
      fetchUnreadCount();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Delete notification
  const handleDeleteClick = (notification) => {
    setNotificationToDelete(notification);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      if (notificationToDelete?._id) {
        await notificationService.deleteNotification(notificationToDelete._id);
        setNotifications(notifications.filter(n => n._id !== notificationToDelete._id));
        fetchUnreadCount();
      }
      setDeleteConfirmOpen(false);
      setNotificationToDelete(null);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Delete all notifications
  const handleDeleteAllNotifications = async () => {
    if (window.confirm('Are you sure you want to delete all notifications?')) {
      try {
        await notificationService.deleteAllNotifications();
        setNotifications([]);
        fetchUnreadCount();
      } catch (error) {
        console.error('Error deleting all notifications:', error);
      }
    }
  };

  // Handle notification click - navigate if actionUrl exists
  const handleNotificationClick = (notification) => {
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
  };

  // Filter notifications by search text
  const filteredNotifications = notifications.filter(notif =>
    notif.title.toLowerCase().includes(searchText.toLowerCase()) ||
    notif.message.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div className="notifications-page-container">
      <div className="notifications-page-content">
        {/* Header */}
        <div className="notifications-page-header">
          <div className="notifications-page-title-section">
            <FaBell className="notifications-page-icon" />
            <div>
              <h1>Notifications</h1>
              {unreadCount > 0 && (
                <p className="notifications-page-unread">
                  {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                </p>
              )}
            </div>
          </div>
          <div className="notifications-page-actions">
            {unreadCount > 0 && (
              <button
                className="notifications-page-btn notifications-page-btn-secondary"
                onClick={handleMarkAllAsRead}
              >
                <FaCheckDouble /> Mark all as read
              </button>
            )}
            {notifications.length > 0 && (
              <button
                className="notifications-page-btn notifications-page-btn-danger"
                onClick={handleDeleteAllNotifications}
              >
                <FaTrash /> Delete all
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="notifications-page-filters">
          <input
            type="text"
            placeholder="Search notifications..."
            className="notifications-page-search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          
          <select
            className="notifications-page-select"
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All Types</option>
            {Object.entries(NOTIFICATION_TYPES).map(([key, value]) => (
              <option key={key} value={key}>{value}</option>
            ))}
          </select>

          <select
            className="notifications-page-select"
            value={filterReadStatus}
            onChange={(e) => {
              setFilterReadStatus(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>
        </div>

        {/* Notifications List */}
        {loading ? (
          <div className="notifications-page-loading">
            <div className="notifications-page-spinner"></div>
            <p>Loading notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="notifications-page-empty">
            <FaBell className="notifications-page-empty-icon" />
            <p>
              {notifications.length === 0 ? 'No notifications yet' : 'No matching notifications'}
            </p>
          </div>
        ) : (
          <>
            <div className="notifications-page-list">
              {filteredNotifications.map((notification) => {
                const severity = getNotificationSeverity(notification);
                return (
                <div
                  key={notification._id}
                  className={`notifications-page-item ${!notification.isRead ? 'unread' : 'read'}`}
                  style={{
                    borderLeft: !notification.isRead ? `4px solid ${getSeverityColor(severity)}` : 'none'
                  }}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notifications-page-item-icon">
                    {getSeverityIcon(severity)}
                  </div>
                  
                  <div className="notifications-page-item-content">
                    <div className="notifications-page-item-header">
                      <h3>{notification.title}</h3>
                      <div className="notifications-page-item-badges">
                        {!notification.isRead && (
                          <span className="notifications-page-badge notifications-page-badge-new">New</span>
                        )}
                        {notification.type && (
                          <span className="notifications-page-badge">
                            {NOTIFICATION_TYPES[notification.type] || notification.type}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="notifications-page-item-message">{notification.message}</p>
                    <span className="notifications-page-item-time">
                      {new Date(notification.createdAt).toLocaleString()}
                    </span>
                  </div>

                  <div className="notifications-page-item-actions">
                    <button
                      className="notifications-page-item-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkAsRead(notification._id, notification.isRead);
                      }}
                      title={notification.isRead ? 'Mark as unread' : 'Mark as read'}
                    >
                      {!notification.isRead ? '✓' : '○'}
                    </button>
                    <button
                      className="notifications-page-item-btn notifications-page-item-btn-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteClick(notification);
                      }}
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              );
              })}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="notifications-page-pagination">
                <button
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                  className="notifications-page-pagination-btn"
                >
                  ← Previous
                </button>
                <span className="notifications-page-pagination-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages, page + 1))}
                  disabled={page === totalPages}
                  className="notifications-page-pagination-btn"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div className="notifications-page-modal-overlay" onClick={() => setDeleteConfirmOpen(false)}>
          <div className="notifications-page-modal" onClick={(e) => e.stopPropagation()}>
            <div className="notifications-page-modal-header">
              <h2>Delete Notification</h2>
              <button
                className="notifications-page-modal-close"
                onClick={() => setDeleteConfirmOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            <div className="notifications-page-modal-content">
              <p>Are you sure you want to delete this notification?</p>
              {notificationToDelete && (
                <div className="notifications-page-modal-preview">
                  <h4>{notificationToDelete.title}</h4>
                  <p>{notificationToDelete.message}</p>
                </div>
              )}
            </div>
            <div className="notifications-page-modal-footer">
              <button
                className="notifications-page-btn notifications-page-btn-secondary"
                onClick={() => setDeleteConfirmOpen(false)}
              >
                Cancel
              </button>
              <button
                className="notifications-page-btn notifications-page-btn-danger"
                onClick={handleConfirmDelete}
              >
                <FaTrash /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
